import type { MapPoint } from './types';
import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import { boundsFromPoints } from './bounds';
import { useProgrammaticMove } from './programmatic-move';

// Refits the map to a point set whenever the set's extent changes. Mount
// as a child of `<MapContainer>`. `MapContainer`'s own `bounds` prop only
// applies on first render, so this watches subsequent changes.
export const FitBounds = ({
  points,
  padding = 40,
  once = false,
}: {
  points: ReadonlyArray<MapPoint>;
  padding?: number;
  // Fit only the first time a non-empty point set arrives, then never
  // again. Bounds-driven hosts (the /products map) use this so the initial
  // "show me my products" fit doesn't fight the user's later pans/zooms.
  once?: boolean;
}) => {
  const map = useMap();
  const programmaticRef = useProgrammaticMove();
  const hasFitRef = useRef(false);
  const bounds = boundsFromPoints(points);
  // Stringify (incl. padding) so the effect fires on coordinate OR padding
  // changes, not on the fresh array identity `boundsFromPoints` returns
  // every render.
  const key = bounds ? JSON.stringify({ bounds, padding }) : null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional value-gating — `key` serializes bounds+padding; `boundsFromPoints` returns a fresh array each render, so direct deps would refit every render.
  useEffect(() => {
    if (!bounds) return;
    if (once && hasFitRef.current) return;
    hasFitRef.current = true;
    // Flag the move as programmatic so a bounds-driven host doesn't mistake
    // this fit for a user pan (which would refetch). The timeout clears the
    // flag if the fit is a no-op (no moveend fires to clear it).
    if (programmaticRef) {
      programmaticRef.current = true;
      window.setTimeout(() => {
        programmaticRef.current = false;
      }, 500);
    }
    // `maxZoom` stops a single-point (zero-area) bbox from snapping to
    // Leaflet's max zoom.
    map.fitBounds(bounds, { maxZoom: 15, padding: [padding, padding] });
  }, [map, key]);
  return null;
};
